import { XMarkIcon } from "@heroicons/react/24/solid";
import { useI18N } from "@sundae/react-hooks";
import { Badge, InfoCircledIcon, Percentage, Text, Tooltip, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC } from "react";
import { IEnhancedValue } from "../../../types/Farm.types";
import { getAssetName } from "../../../utils/assets.utils";
import AssetWithAssetName from "../../AssetWithAssetName";
import PoolComponent from "../../PoolComponent";
import LockedPositionValueInput from "../LockedPositionValueInput";

interface ILockedPositionItemProps {
  handleUpdate(assetId: string, value: string): void;
  handleRemove(assetIdToRemove: string): void;
  isInputBelowAdaMinLimit: boolean;
  positionValue: IEnhancedValue;
  showRemove?: boolean;
  hra?: number;
}

const LockedPositionItem: FC<ILockedPositionItemProps> = ({
  handleUpdate,
  handleRemove,
  isInputBelowAdaMinLimit,
  positionValue,
  showRemove = true,
  hra,
}) => {
  const { t } = useI18N("farms");
  return (
    <div className="flex w-full items-center gap-2">
      {showRemove && (
        <Tooltip
          trigger={
            <XMarkIcon
              className="h-3 w-3 cursor-pointer fill-current text-error dark:!text-error-200"
              onClick={() => handleRemove(positionValue.assetID)}
              role="button"
            />
          }
          tooltipLabel={t("portfolio.positions.description", { context: "add" })}
        />
      )}
      <div
        className={cx(
          "rounded-lg",
          "focus-within:!border-primary dark:focus-within:!border-primary",
          toolkitCx.layout.transition.colors,
          toolkitCx.layout.border.default,
          toolkitCx.layout.border.flashOnFocus,
          toolkitCx.layout.border.weight.default,
          toolkitCx.layout.spacing.both.md,
          {
            "w-full": showRemove,
            "ml-auto w-[calc(100%-22px)]": !showRemove,
          }
        )}
      >
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col items-start justify-center gap-2">
            {positionValue.pool ? (
              <PoolComponent
                pool={positionValue.pool}
                inline
                badges={{ ident: false, lpFee: false, version: true }}
              />
            ) : (
              <AssetWithAssetName
                assets={positionValue.asset.metadata}
                assetName={getAssetName(positionValue.asset.metadata)}
              />
            )}
            {hra && (
              <div className="flex items-center justify-center">
                <Tooltip
                  trigger={
                    <Badge size="xs" variant="secondary" className="!rounded-r-none">
                      <Text
                        tag="span"
                        size="2xs"
                        className="inline-flex items-center justify-start gap-1"
                      >
                        {t("hra")}
                        <InfoCircledIcon className="h-3 w-3" />
                      </Text>
                    </Badge>
                  }
                  tooltipLabel={t("hraLegalText")}
                />
                <Badge size="xs" variant="gradient" className="!inline-flex !rounded-l-none">
                  <Tooltip trigger={t("hra")} tooltipLabel={t("hraLegalText")} />
                  <Percentage size="2xs" value={hra} variant="forceLight" />
                </Badge>
              </div>
            )}
          </div>

          <div className="flex flex-1 items-center justify-end gap-2">
            <LockedPositionValueInput
              handleUpdate={(value) => handleUpdate(positionValue.asset.metadata.assetId, value)}
              isInputBelowAdaMinLimit={isInputBelowAdaMinLimit}
              positionValue={positionValue}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LockedPositionItem;
